import { tutorials, type ITutorial } from '@/components/tutorial/tutorialSteps'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type TPickerVariant = 'default' | 'returning'

export const useTutorialStore = defineStore('tutorial', () => {
  const isActive = ref(false)
  const celebrating = ref(false)
  const pickerOpen = ref(false)
  const pickerVariant = ref<TPickerVariant>('default')
  const pickerIsPrompt = ref(false)
  const currentIndex = ref(0)
  const activeTutorialId = ref(tutorials[0].id)
  const overlayHosts = ref(0)

  const isSupported = computed(() => !useDetectBreakpoint('xs'))

  const activeTutorial = computed<ITutorial>(
    () => tutorials.find((t) => t.id === activeTutorialId.value) ?? tutorials[0]
  )
  const steps = computed(() => activeTutorial.value.steps)
  const stepCount = computed(() => steps.value.length)
  const currentStep = computed(() => steps.value[currentIndex.value])
  const activeStep = computed(() =>
    isActive.value && !celebrating.value ? currentStep.value : undefined
  )
  const isLast = computed(() => currentIndex.value === stepCount.value - 1)
  const stepNumber = computed(() => currentIndex.value + 1)
  const overlayMounted = computed(() => overlayHosts.value > 0)
  const holdReplyBoxOpen = computed(() => activeStep.value?.expandReplyBox)

  const registerOverlayHost = () => {
    overlayHosts.value += 1
    return () => {
      overlayHosts.value -= 1
    }
  }

  const start = (tutorialId?: string) => {
    if (!isSupported.value) return
    if (tutorialId) activeTutorialId.value = tutorialId
    currentIndex.value = 0
    celebrating.value = false
    pickerOpen.value = false
    isActive.value = true
  }

  const next = () => {
    if (!isActive.value || celebrating.value) return
    if (isLast.value) {
      celebrating.value = true
      return
    }
    currentIndex.value += 1
  }

  const back = () => {
    if (!isActive.value || celebrating.value || currentIndex.value === 0) return
    currentIndex.value -= 1
  }

  const close = () => {
    isActive.value = false
    celebrating.value = false
  }

  const openPicker = (
    variant: TPickerVariant = 'default',
    { asPrompt = false }: { asPrompt?: boolean } = {}
  ) => {
    if (!isSupported.value) return
    pickerVariant.value = variant
    pickerIsPrompt.value = asPrompt
    isActive.value = false
    celebrating.value = false
    pickerOpen.value = true
  }

  const closePicker = () => {
    pickerOpen.value = false
  }

  return {
    isSupported,
    isActive,
    celebrating,
    pickerOpen,
    pickerVariant,
    pickerIsPrompt,
    currentIndex,
    activeTutorialId,
    activeTutorial,
    steps,
    stepCount,
    currentStep,
    activeStep,
    stepNumber,
    overlayMounted,
    holdReplyBoxOpen,
    registerOverlayHost,
    start,
    next,
    back,
    close,
    openPicker,
    closePicker
  }
})
